import classNames from "./SidePanel.module.css";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useLocalParticipant,
} from "@livekit/components-react";
import { createRef, useCallback, useEffect, useState } from "react";
import useUser, { useRenderLanguage, useUserMetadata } from "../../hooks/user";
import UserLanguageSelect from "../common/UserLanguageSelect";
import Voice from "../common/Voice";
import Captions from "../common/Captions";
import { usePublisherToken } from "../../hooks/token";
import * as teams from "@microsoft/teams-js";
import { useInMeeting, useIsInTeamsMobile, useTeamsContext } from "../../hooks/teams";
import Loader from "../common/Loader";
import { getLanguageFlag, getLanguageName, getTextLanguage } from "../../types";
import { useAxios } from "../../hooks/axios";
import api, { Organization, Room } from "../../hooks/api-new";
import useCaptions from "../../hooks/captions";
import { IoEllipsisHorizontal } from "react-icons/io5";
import { useDominantSpeakerLanguage } from "../../hooks/speaker";
import { usePollRoomMinutesRemaining, useRoom } from "../context/room/room";
import CreateOrEditRoom from "../routes/room/CreateOrEditRoom";
import { OrganizationProvider } from "../context/organization/OrganizationProvider";
import { useDefaultSettings } from "../../hooks/settings";
import { useGetStoredSettings } from "../../hooks/layout";
import Button from "../common/Button";
import Header from "../Header";
import { MdOutlineClosedCaption } from "react-icons/md";
import UpgradeNow from "../common/UpgradeNow";

const Translator = (props: { room: Room; organization: Organization }) => {
  useUserMetadata();
  useDefaultSettings();
  useGetStoredSettings();
  usePollRoomMinutesRemaining();
  const teamsContext = useTeamsContext();
  const isInTeamsMobile = useIsInTeamsMobile();

  const [captions] = useCaptions({ teamsContext: teamsContext });
  const [user, setUser] = useUser();
  const { localParticipant } = useLocalParticipant();

  const pipPadding = 20;
  const pipFontGap = 10;
  const totalLinesToRender = 4;
  const pipWidth = 600;
  const pipFontSize = 24;
  const pipHeight =
    pipPadding * 2 +
    pipFontSize * totalLinesToRender +
    pipFontGap * (totalLinesToRender - 1);

  const [overlayActive, setOverlayActive] = useState(false);

  // Render captions to the canvas for pip mode
  const renderLanguage = useRenderLanguage();
  const canvas = createRef<HTMLCanvasElement>();
  useEffect(() => {
    let animationFrameId: number | undefined = undefined;
    const render = async () => {
      if (!canvas.current) {
        return;
      }

      const scale = window.devicePixelRatio;
      canvas.current.width = Math.floor(pipWidth * scale);
      canvas.current.height = Math.floor(pipHeight * scale);

      const context = canvas.current.getContext("2d");
      if (!context) {
        return;
      }
      // Normalize coordinate system to use CSS pixels.
      context.scale(scale, scale);

      context.fillStyle = "#101010";
      context.fillRect(0, 0, canvas.current.width, canvas.current.height);

      // Determine which captions to render
      const lastCaptions = Object.values(captions)
        // Filter captions for local participant
        .filter((caption) => caption.identity !== localParticipant.identity)
        .slice(-totalLinesToRender);

      const textLanguage =
        (renderLanguage && getTextLanguage(renderLanguage)) ||
        props.room.languages[0];
      if (!textLanguage) {
        return;
      }

      const captionsToRender = lastCaptions
        .filter((caption) => !!caption.translations[textLanguage])
        .map(
          (caption) =>
            `${caption.name.split(" ")[0] || caption.name}:\t${caption.translations[textLanguage]
            }`
        );

      context.fillStyle = "white";
      context.font = `${pipFontSize}px sans-serif`;
      context.textAlign = "left";
      context.textBaseline = "bottom";
      context.textRendering = "optimizeLegibility";

      captionsToRender
        .reduce((result: string[], text: string) => {
          let currentLine = "";
          const words = text.split(" ");
          for (const word of words) {
            // If entire word takes line, split by char
            const measureWord = context.measureText(word);
            if (measureWord.width > pipWidth - pipPadding * 2) {
              for (const char of word) {
                const measure = context.measureText(currentLine + char);
                if (measure.width > pipWidth - pipPadding * 2) {
                  result.push(currentLine);
                  currentLine = char;
                } else {
                  currentLine += char;
                }
              }
            }
            // Split by empty space
            else {
              const measure = context.measureText(currentLine + word);
              if (measure.width > pipWidth - pipPadding * 2) {
                result.push(currentLine.trim());
                currentLine = word + " ";
              } else {
                currentLine += word + " ";
              }
            }
          }

          if (currentLine.length > 0) {
            result.push(currentLine.trim());
          }

          return result;
        }, [])
        .slice(-totalLinesToRender)
        .forEach((line, index) => {
          context.fillStyle = "white";
          context.fillText(
            line,
            pipPadding,
            pipPadding + (index + 1) * pipFontSize + index * pipFontGap
          );
          // Debug lines
          // context.fillStyle = "red";
          // context.fillRect(pipPadding, 0, 1, pipHeight);
          // context.fillRect(pipWidth - pipPadding, 0, 1, pipHeight);
          // context.fillRect(
          //   0,
          //   pipPadding + index * pipFontSize + index * pipFontGap,
          //   canvas.current?.width || 0,
          //   1
          // );
          // context.fillRect(
          //   0,
          //   (index + 1) * pipFontSize + pipPadding + index * pipFontGap,
          //   canvas.current?.width || 0,
          //   1
          // );
        });

      animationFrameId = requestAnimationFrame(render);
    };
    render();
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [
    canvas,
    captions,
    renderLanguage,
    pipFontSize,
    pipPadding,
    pipWidth,
    pipHeight,
    localParticipant.identity,
    props.room.languages,
  ]);

  const toggleOverlay = useCallback(() => {
    if (!canvas.current) {
      return;
    }
    const stream = canvas.current.captureStream();
    const videoElement = document.createElement("video");
    videoElement.hidden = true;
    videoElement.muted = true;
    videoElement.autoplay = true;
    videoElement.srcObject = stream;

    const onRemove = () => {
      stream.getTracks().forEach((t) => t.stop());
      videoElement.remove();
      setOverlayActive(false);
    };

    videoElement.onloadedmetadata = async () => {
      videoElement.onleavepictureinpicture = () => {
        onRemove();
      };
      try {
        await videoElement.requestPictureInPicture();
        setOverlayActive(true);
      } catch {
        onRemove();
      }
    };

    videoElement.play();
  }, [canvas]);

  const inMeeting = useInMeeting();

  // Default voice on when set in room config
  useEffect(() => {
    if (props.room?.default_voice_on) {
      setUser((prevState) => ({
        ...prevState,
        metadata: {
          ...prevState.metadata,
          // Don't turn on with auto detect selected
          voice:
            prevState.metadata.selectedLanguage !== "autodetect"
              ? "ai"
              : undefined,
        },
      }));
    }
  }, [props.room?.default_voice_on, setUser]);

  useEffect(() => {
    setUser((prevState) => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        // Check if selected language is in room, if not set selected language to english
        selectedLanguage: props.room.languages.includes(
          prevState.metadata.selectedLanguage
        )
          ? prevState.metadata.selectedLanguage
          : props.room.languages.includes("en-US")
            ? "en-US"
            : props.room.languages[0],
        participantType: "viewer",
        botParticipantId: teamsContext?.user?.id,
      },
    }));
  }, [setUser, teamsContext?.user?.id, props.room.languages]);

  const dominantSpeakerLanguage = useDominantSpeakerLanguage();

  useEffect(() => {
    if (!inMeeting) {
      return;
    }
    teams.meeting.getIncomingClientAudioState((err, alreadyMuted) => {
      if (err) {
        console.error(err);
        return;
      }
      if (
        // Toggle meeting audio on when muted and ai voice turned off
        (alreadyMuted &&
          (!user.metadata.voice ||
            !dominantSpeakerLanguage ||
            dominantSpeakerLanguage === renderLanguage)) ||
        // Toggle meeting audio off when not muted, ai voice turned on, and dominant speaker speaking different language
        (!alreadyMuted &&
          !!user.metadata.voice &&
          !!dominantSpeakerLanguage &&
          dominantSpeakerLanguage !== renderLanguage)
      ) {
        console.debug(
          "toggle teams audio...",
          dominantSpeakerLanguage,
          renderLanguage
        );
        teams.meeting.toggleIncomingClientAudio((err) => {
          if (err) {
            console.error(err);
            return;
          }
        });
      }
    });
  }, [
    inMeeting,
    user.metadata.voice,
    dominantSpeakerLanguage,
    user.metadata.selectedLanguage,
    renderLanguage,
  ]);

  const openSettingsDialog = useCallback(() => {
    teams.dialog.url.open({
      url: `${window.location.origin}/#/teams/settings/${props.room.id}`,
      size: {
        width: 600,
        height: 400,
      },
    });
  }, [props.room]);

  return (
    <>
      <UpgradeNow
        organization={props.organization}
        className={classNames.HeaderUpgradeNow}
        goToCalendly={true}
      />
      <div className={classNames.Translator}>
        <canvas
          style={{ display: "none" }}
          ref={canvas}
          width={pipWidth}
          height={pipHeight}
        />
        <RoomAudioRenderer />

        <div className={classNames.Captions}>
          <Captions renderLanguage={renderLanguage} />
        </div>

        <div className={classNames.Toolbar}>
          <UserLanguageSelect
            languages={[
              ...(props.organization.autodetect_enabled ? ["autodetect"] : []),
              ...props.room.languages,
            ]}
          />
          {props.organization.voice_translation_enabled && (
            <Voice hideText={false} text="Voice" teams={true} />
          )}
          {/* Hide overlay for teams mobile */}
          {!isInTeamsMobile && (
            <Button
              color={overlayActive ? "primary" : "gray"}
              backgroundColor="black"
              borderColor={"transparent"}
              onClick={toggleOverlay}
            >
              <MdOutlineClosedCaption />
              Overlay
            </Button>
          )}
          <Button
            color="white"
            backgroundColor="black"
            borderColor="transparent"
            onClick={openSettingsDialog}
          >
            <IoEllipsisHorizontal />
          </Button>
        </div>
      </div>
    </>
  );
};

function ProExperience(props: { room: Room; organization: Organization }) {
  const [invitingBot, setInvitingBot] = useState(false);
  const inMeeting = useInMeeting();

  const axios = useAxios();
  const { token } = usePublisherToken(axios);

  const [botActive, setBotActive] = useState<boolean | undefined>();
  const getBotStatus = api.useGetBotStatus(axios);
  const inviteBot = api.useInviteBot(axios);

  useEffect(() => {
    const getBotStatusRequest = async () => {
      const response = await getBotStatus({
        roomId: props.room.id,
      });
      setBotActive(response.data.active);
    };

    const getBotStatusRequestInterval = setInterval(() => {
      if (!botActive) {
        console.log("got bot status...");
        getBotStatusRequest();
      }
    }, 5 * 1000);

    getBotStatusRequest();

    return () => {
      clearInterval(getBotStatusRequestInterval);
    };
  }, [getBotStatus, props.room.id, botActive]);

  const [meetingUrl, setMeetingUrl] = useState(props.room.meeting_url || "");

  const parseJoinInfo = useCallback((joinInfo: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(joinInfo, "text/html");

    // Find the link with a specific id
    let joinLink = doc.getElementById("meet_invite_block.action.join_link") as
      | HTMLAnchorElement
      | null
      | undefined;

    if (!joinLink) {
      const links = Array.from(doc.getElementsByTagName("a"));
      joinLink = links.find((link) => link.href.includes("meetup-join"));
    }

    if (!joinLink) {
      throw new Error("Failed to parse Teams join info!");
    }

    return joinLink.href;
  }, []);

  const openInviteBotHelpDialog = useCallback(() => {
    teams.dialog.url.open({
      url: `${window.location.origin}/#/teams/invite-bot-help`,
      size: {
        width: 600,
        height: 400,
      },
    });
  }, []);

  const [user] = useUser();

  if (botActive === undefined) {
    return <Loader />;
  }

  if (invitingBot) {
    return (
      <div className={classNames.SidePanel}>
        <div className={classNames.InviteBot}>
          <Loader inline={true} style={{ marginTop: "24px" }} />
          <h1>Your AI translator is joining the meeting</h1>
          <h2>
            This might take a few seconds. Once it's ready, we'll share a link
            in the meeting chat. The real-time translation will appear on this
            tab when you join.
          </h2>
        </div>
      </div>
    );
  }

  if (!inMeeting) {
    if (!user.auth) {
      return (
        <>
          <Header hideUserNav={true} disableLogoClick={true} />
          <div className={classNames.UnauthNotInMeeting}>
            <h2>Please join the call to experience real-time translation</h2>
            <div className={classNames.Languages}>
              <label>Languages</label>
              {props.room.languages.map((language) => (
                <div key={language}>
                  {getLanguageFlag(language)} {getLanguageName(language)}
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <OrganizationProvider>
          <Header hideUserNav={true} disableLogoClick={true} />
          <CreateOrEditRoom />
        </OrganizationProvider>
      </>
    );
  }

  // Invite meeting bot
  if (!botActive) {
    return (
      <>
        <div className={classNames.SidePanel}>
          <div className={classNames.InviteBot}>
            <h1>Invite your AI translator to the meeting</h1>
            <div className={classNames.InviteBotOptions}>
              <h2>Copy and paste the join info link below</h2>
              <input
                type="url"
                placeholder="Enter your meeting URL here"
                value={meetingUrl}
                onPaste={(e) => {
                  // If html, parse join info
                  if (e.clipboardData.types.includes("text/html")) {
                    // Prevent onChange if handling paste
                    e.preventDefault();
                    const input = e.clipboardData.getData("text/html");
                    const url = parseJoinInfo(input);
                    if (url) {
                      setMeetingUrl(url);
                    }
                  }
                }}
                onChange={(e) => {
                  const input = e.target.value;
                  // If html, parse join info
                  if (input.includes("<")) {
                    const url = parseJoinInfo(input);
                    if (url) {
                      setMeetingUrl(url);
                    }
                  } else {
                    setMeetingUrl(input);
                  }
                }}
              />
              <div
                className={classNames.InviteBotHelp}
                onClick={() => openInviteBotHelpDialog()}
              >
                Where can I find this?
              </div>
            </div>
            <div className={classNames.InviteBotOptions}>
              <h2 style={{ fontWeight: 600 }}>Languages</h2>
              <div className={classNames.PreCallConfigLanguages}>
                {props.room.languages
                  .sort((a, b) =>
                    a === "autodetect" ? 1 : b === "autodetect" ? -1 : 0
                  )
                  .map((language) => (
                    <div key={language}>{`${getLanguageFlag(
                      language
                    )} ${getLanguageName(language)}`}</div>
                  ))}
              </div>
            </div>
            <Button
              disabled={
                !meetingUrl.length ||
                props.organization.translation_seconds_remaining <= 0
              }
              onClick={
                meetingUrl.length
                  ? async () => {
                    try {
                      setInvitingBot(true);
                      await inviteBot({
                        roomId: props.room.id,
                        meetingUrl: meetingUrl,
                      });
                      setBotActive(true);
                    } finally {
                      setTimeout(() => {
                        setInvitingBot(false);
                      }, 2 * 1000);
                    }
                  }
                  : undefined
              }
            >
              Invite AI translator
            </Button>
            <UpgradeNow organization={props.organization} goToCalendly={true} />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={classNames.SidePanel}>
      <LiveKitRoom
        audio={false}
        video={false}
        connect={true}
        token={token}
        connectOptions={{ autoSubscribe: false }}
        serverUrl={import.meta.env.VITE_LIVEKIT_URL}
      >
        <Translator room={props.room} organization={props.organization} />
      </LiveKitRoom>
    </div>
  );
}

function SidePanel() {
  const { room, organization, loading } = useRoom();

  if (!room || !organization || loading) {
    return <Loader />;
  }

  return <ProExperience room={room} organization={organization} />;
}

export default SidePanel;
